import {
  START_ELEMENTS,
  STOP_ELEMENTS,
  toOndoCodeView,
  type CodeFacts,
} from "@ondo/shared";
import { ONDO_CODE } from "./ondoCode.constants.ts";
import "./ondoCode.css";

/**
 * A work's ONDO code, drawn.
 *
 * The view half of `ondoCode.ts`: it renders what `toOndoCodeView` decided and
 * decides nothing itself. There is no condition in here a product person would
 * recognise as a rule — the state, the add-on and the caption are all settled in
 * the model and unit-tested there.
 *
 * THE LAYOUT IS AN EAN'S, because the audience already owns one. An ISBN with
 * its price add-on is on the back of every catalogue raisonné: the end symbols drop
 * below the text so the printed code sits in its own well, and the supplement stands
 * off to the right. Nobody has to be taught how to look at it.
 *
 * ## What changed when the bars became a real barcode — 4 October
 *
 * The encoding is now genuine Code 128 (see `domain/ondoCode.ts`), and three things
 * about this file follow from that:
 *
 * 1. **There is no centre guard.** Code 128 has a start symbol, data, a check
 *    character and a stop symbol, and nothing in the middle. So the printed code is
 *    one centred string under the bars rather than two halves in two wells — the
 *    grouping is carried by the hyphen, which is what it is for.
 * 2. **`OND` is still printed outside the bars, and now the bars encode it too.** That
 *    is deliberate: the bars carry the normalised code so a stranger's scanner yields
 *    something whole, and the outside prefix stays because it reads well. It is a
 *    typographic convention, not a claim about what is encoded.
 * 3. **The mark is about 1.6× wider at the same height** — 156 modules against the old
 *    99. A real Code 128 is simply longer than something that only looked like one.
 *    The bars are correspondingly thinner on screen, which is the one thing to look at
 *    in a browser before this ships.
 *
 * NOTHING RENDERS WHEN THE CODE CANNOT BE DRAWN. `WorkBarcode`, which this
 * replaces, took the same path for a photograph it could not sample.
 */
export function OndoCode({
  code,
  facts,
  size = "row",
}: {
  code: string;
  facts: CodeFacts;
  /** `record` prints the code and the state word; `row` is bars and caption only. */
  size?: "row" | "record";
}) {
  const view = toOndoCodeView(code, facts);
  if (!view) return null;

  const { encoded, state, word, caption, left, right } = view;
  const full = size === "record";
  const { height, guardExtend, prefix, addOnGap, quiet, addOnTop, wordBand, text } = ONDO_CODE;

  // The start and stop symbols drop below the bars, so the printed code sits in its
  // own well. A borrowing from EAN's look; Code 128 has no guard bars of its own.
  const extend = full ? guardExtend : 0;
  const startAt = full ? prefix : 0;

  const bars: React.ReactNode[] = [];
  const guardElements = guardSet(encoded.main.length);

  let x = startAt;
  let isBar = true;
  encoded.main.forEach((width, element) => {
    if (isBar) {
      bars.push(
        <rect
          key={`m${element}`}
          x={x}
          y={0}
          width={width}
          height={height + (guardElements.has(element) ? extend : 0)}
        />,
      );
    }
    x += width;
    isBar = !isBar;
  });

  let addStart = 0;
  let addEnd = 0;
  if (encoded.add.length) {
    x += addOnGap;
    addStart = x;
    isBar = true;
    encoded.add.forEach((width, element) => {
      if (isBar) {
        bars.push(
          <rect
            key={`a${element}`}
            x={x}
            y={addOnTop}
            width={width}
            height={height + extend - addOnTop}
          />,
        );
      }
      x += width;
      isBar = !isBar;
    });
    addEnd = x;
  }

  const width = x + quiet;
  const totalHeight = height + extend + (full ? wordBand : 0);

  return (
    <div className={`ondo-code ondo-code--${size}`}>
      <svg
        className={`ondo-code__mark ondo-code__mark--${state}`}
        viewBox={`0 0 ${width} ${totalHeight}`}
        role="img"
        aria-label={view.label}
      >
        <g className="ondo-code__bars">{bars}</g>

        {full ? (
          <>
            {/* Outside the bars, like an EAN's leading digit. */}
            <text className="ondo-code__prefix" x={prefix - 1.5} y={height + extend} fontSize={text.prefix} textAnchor="end">
              OND
            </text>
            {/*
              ONE centred string, because Code 128 has no centre guard to split two
              halves into two wells. The hyphen carries the grouping.
            */}
            <text
              className="ondo-code__payload"
              x={startAt + dataSpan(encoded.main) / 2}
              y={height + extend + 0.2}
              fontSize={text.payload}
              textAnchor="middle"
            >
              {left}-{right}
            </text>
            <text className="ondo-code__word" x={startAt} y={totalHeight - 0.6} fontSize={text.word}>
              {word}
            </text>
          </>
        ) : null}

        {caption && encoded.add.length ? (
          <text
            className="ondo-code__caption"
            x={(addStart + addEnd) / 2}
            y={addOnTop - 2.8}
            fontSize={text.caption}
            textAnchor="middle"
          >
            {caption}
          </text>
        ) : null}
      </svg>
    </div>
  );
}

/**
 * Which elements drop below the bars, by index.
 *
 * The start symbol and the stop symbol, which between them give the printed code a
 * well to sit in. **Code 128 has no guard bars** in EAN's sense — this is a
 * typographic borrowing, not part of the symbology, and a scanner neither needs nor
 * notices it.
 *
 * Taken from the encoder's own exported element counts rather than written down, so a
 * change to the symbology cannot leave this pointing at the wrong bars.
 */
function guardSet(length: number): Set<number> {
  const out = new Set<number>();
  for (let i = 0; i < START_ELEMENTS; i += 1) out.add(i);
  for (let i = 1; i <= STOP_ELEMENTS; i += 1) out.add(length - i);
  return out;
}

/**
 * How many modules the data occupies, start and stop excluded.
 *
 * Used to centre the printed code under the characters it describes rather than under
 * the whole symbol — the stop pattern is thirteen modules and would pull the text
 * visibly right of the bars it belongs to.
 */
function dataSpan(main: number[]): number {
  const before = main
    .slice(0, START_ELEMENTS)
    .reduce((a, b) => a + b, 0);
  const data = main
    .slice(START_ELEMENTS, main.length - STOP_ELEMENTS)
    .reduce((a, b) => a + b, 0);
  return before + data;
}
